import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { FileText, Plus, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CLAIMS } from "@/constants/testIds";
import { useAuth } from "@/context/AuthContext";

const STATUS_COLOR = {
  submitted: "bg-muted text-foreground",
  validated: "bg-primary text-primary-foreground",
  in_transit: "bg-primary text-primary-foreground",
  received: "bg-primary text-primary-foreground",
  diagnosed: "bg-primary text-primary-foreground",
  repairing: "bg-secondary text-teal",
  repaired: "bg-secondary text-teal",
  returned: "bg-secondary text-teal",
  closed: "bg-primary/10 text-primary",
  rejected: "bg-destructive/10 text-destructive",
};

const DECISION_BADGE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-secondary/15 text-teal",
  rejected: "bg-destructive/10 text-destructive",
  more_info: "bg-primary/10 text-primary",
};

export default function Claims() {
  const { user } = useAuth();
  const [claims, setClaims] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    const { data } = await api.get("/claims");
    setClaims(data);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const isAdmin = user?.role === "admin" || user?.role === "support";
  const pendingCount = claims.filter((c) => c.admin_decision === "pending").length;
  const filtered = isAdmin && filter === "pending"
    ? claims.filter((c) => c.admin_decision === "pending")
    : claims;

  return (
    <div className="space-y-8">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="overline text-secondary">Claims</div>
          <h1 className="mt-1 font-display text-4xl font-light tracking-tight">
            {user?.role === "support" || user?.role === "admin" ? "All claims" : "Your claims"}
          </h1>
        </div>
        {(user?.role === "customer" || user?.role === "b2b_admin" || user?.role === "b2b_agent") && (
          <Link to="/app/claims/new">
            <Button data-testid={CLAIMS.newBtn} className="bg-primary text-primary-foreground">
              <Plus className="w-4 h-4 mr-2" /> File a claim
            </Button>
          </Link>
        )}
      </div>

      {isAdmin && (
        <div className="flex items-center gap-2 flex-wrap" data-testid="claims-filter-bar">
          <button
            onClick={() => setFilter("all")}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === "all" ? "bg-primary text-primary-foreground border-primary" : "bg-card border-border hover:border-primary/40"}`}
            data-testid="claims-filter-all"
          >
            All claims ({claims.length})
          </button>
          <button
            onClick={() => setFilter("pending")}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${filter === "pending" ? "bg-amber-500 text-white border-amber-500" : "bg-card border-border hover:border-amber-400"}`}
            data-testid="claims-filter-pending"
          >
            Pending decision ({pendingCount})
          </button>
        </div>
      )}

      <div data-testid={CLAIMS.list} className="space-y-3">
        {filtered.length === 0 && (
          <div className="text-center py-16 bg-card border border-dashed border-border rounded-2xl">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground/40" />
            <div className="mt-4 font-display text-xl">
              {filter === "pending" ? "No claims pending decision" : "No claims yet"}
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              {filter === "pending" ? "You're all caught up." : "Filed claims will appear here."}
            </p>
          </div>
        )}
        {filtered.map(c => (
          <Link key={c.id} to={`/app/claims/${c.id}`} data-testid={CLAIMS.claimCard(c.id)}
                className="flex items-center gap-4 p-4 lg:p-5 bg-card border border-border rounded-2xl hover:-translate-y-0.5 transition-transform">
            <div className="w-12 h-12 rounded-xl bg-primary/8 text-primary flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <div className="font-medium">{c.case_number}</div>
                <span className={`text-[10px] overline px-2 py-0.5 rounded-full ${STATUS_COLOR[c.status] || "bg-muted"}`}>{c.status.replace("_"," ")}</span>
                {c.admin_decision && c.admin_decision !== "approved" && (
                  <span className={`text-[10px] overline px-2 py-0.5 rounded-full ${DECISION_BADGE[c.admin_decision] || "bg-muted"}`}>
                    {c.admin_decision === "more_info" ? "info requested" : c.admin_decision}
                  </span>
                )}
                {c.ai_severity && (
                  <span className="text-[10px] overline px-2 py-0.5 rounded-full bg-secondary/15 text-secondary">
                    {c.ai_severity}
                  </span>
                )}
                {c.fraud_flag && <span className="text-[10px] overline px-2 py-0.5 rounded-full bg-destructive/10 text-destructive">flagged</span>}
              </div>
              <div className="text-xs text-muted-foreground mt-1 capitalize">
                {c.issue_category} · {new Date(c.created_at).toLocaleDateString()}
                {c.ai_estimate_gbp ? ` · est. £${c.ai_estimate_gbp}` : ""}
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-muted-foreground" />
          </Link>
        ))}
      </div>
    </div>
  );
}
